import axios from 'axios';
import { buildingFieldsToInclude, deviceFieldsToInclude, formatObjectFields, transformBuildingDetails, transformDeviceDetails } from './transform';
import API_BASE_URL from '../../../Constants';

/**
 * Sends the user input to the backend chatbot endpoint and returns the response.
 */
export const fetchBotResponse = async (userInput) => {
  console.log(`🤖 Sending user input to chatbot: ${userInput}`);
  try {
    const response = await axios.post(`${API_BASE_URL}/api/chatbot/response`, { userInput });
    return response.data;
  } catch (error) {
    console.error("Error fetching chatbot response:", error);
    return "Something went wrong. Please try again."
  }
};

/**
 * Sends an SMS message to a given phone number.
 * 
 * @param {string} phoneNumber - Recipient's 10-digit phone number.
 * @param {string} message - The message to send.
 * @returns {Promise<object>} - A confirmation message.
 */
export const sendSMS = async (phoneNumber, message) => {
  // console.log(`📲 Sending SMS to ${phoneNumber}: "${message}"`);
  let userInput = `send ${phoneNumber} a text message that "${message}"`;
  return await fetchBotResponse(userInput);

};

/**
 * Sends an approval email.
 * 
 * @param {string} email - Recipient's email address.
 * @param {string} emailType - The type of email (0 = Contract, 1 = Work, 2 = Inspection).
 * @returns {Promise<object>} - A confirmation message.
 */
export const sendEmail = async (email, emailType) => {
  // console.log(`📧 Sending Email to ${email} | Type: ${emailType}`);

  if (!isNaN(emailType)) {
    let emailTypeMap = { 0: "contract", 1: "work", 2: "inspections" };
    emailType = emailTypeMap[emailType];
  }

  // send inspections proposal to (.+)$
  let userInput = `send ${emailType} proposal to ${email}`;
  return await fetchBotResponse(userInput);

};

/**
 * Fetches data from the database based on type and ID.
 * 
 * @param {number} type - 0 for customer data, 1 for building data.
 * @param {string} id - The unique identifier or query filter.
 * @returns {Promise<object>} - The fetched data.
 */
export const fetchData = async (type, id) => {

  let typeMap = { "0": "customer", "1": "building", "customer": "customer", "building": "building", "customer data": "customer", "building data": "building" };
  type = typeMap[type];
  let userInput = `run sql query: SELECT * FROM ${type}s WHERE ${type}ID = ${id}`;
  return await fetchBotResponse(userInput);
};


/**
 * Fetches building details based on the provided address.
 * 
 * @param {string} streetNum - The street number.
 * @param {string} streetName - The street name.
 * @param {string} borough - The borough name.
 * @returns {Promise<object>} - The building details.
 */
export const fetchBuildingByAddress = async (streetNum, streetName, borough) => {
  let userInput = `Address ${streetNum}, ${streetName}, ${borough}`;

  let response = await fetchBotResponse(userInput);

  if (response?.PropertyDetails?.BIN === "") {
    return { text: `No building found with the address:\n\n ${streetNum}, ${streetName}, ${borough}`, BIN: null, buildingDetails: null };
  }

  let BIN = response?.PropertyDetails?.BIN;
  let text = formatObjectFields(response?.PropertyDetails, buildingFieldsToInclude);
  return { text, BIN, response: response?.PropertyDetails };

};

/**
 * Fetches building details based on the provided BIN (Building Identification Number).
 * 
 * @param {string} bin - The 7-digit BIN number.
 * @returns {Promise<object>} - The building details.
 */
export const fetchBuildingByBIN = async (bin) => {

  let userInput = `BIN ${bin}`;
  let response = await fetchBotResponse(userInput);


  if (response?.PropertyDetails?.BIN === "") {

    return { text: `No building found with the BIN: ${bin}`, BIN: null, buildingDetails: null };
  }

  let BIN = bin;
  let text = formatObjectFields(response?.PropertyDetails, buildingFieldsToInclude);
  return { BIN, text, response: response?.PropertyDetails };
};



/**
 * Fetches building details based on the provided BIN (Building Identification Number).
 * 
 * @param {string} bin - The 7-digit BIN number.
 * @returns {Promise<object>} - The devices details.
 */
export const fetchBuildingDevices = async (bin) => {

  let userInput = `DEVICES ${bin}`;
  let devices = await fetchBotResponse(userInput);

  if (devices.length === 0) {
    return { text: `No Devices found for building with the BIN: ${bin}`, devices };
  }

  let text = devices.map((device, index) => `------------------------DEVICE # ${index + 1}------------------------\n \n` + formatObjectFields(device, deviceFieldsToInclude)).join('\n \n');
  return { text: text, devices };


};

const addOpenDataBuildingAndDevices = async (data) => {

  try {
    const response = await fetch(`${API_BASE_URL}/api/setup/buildings/chatbot/add-opendata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return await response.text();
  } catch (error) {
    console.error("Error adding building and devices:", error);
    return "Error adding building and devices."
  }

}

export const addBuildingAndDevices = async (buildingDetails, devices) => {

  let data = {
    "building": transformBuildingDetails(buildingDetails),
    "devices": devices.map(transformDeviceDetails)
  }
  return addOpenDataBuildingAndDevices(data);

}


export const rewriteConvo = (helpConvoHistory) => {
  let convo = "\n\nCONVERSATION:\n\n";
  helpConvoHistory.forEach((item) => {
    if (item.sender === "user") {
      convo += `User: ${item.text}\n`;
    } else {
      convo += `Assistant: ${item.text}\n`;
    }
  });
  return convo;
}


export const createInspectionsNotification = async (agencyId, inspYear) => {
  let userInput = `INSPECTION ${agencyId} ${inspYear}`;
  return await fetchBotResponse(userInput);

}